import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { useFloatingNavigation } from "../utils/hooks";
import wsdLogo from "./../assets/img/logo_wsd.png";
import LogoMini from "./../assets/img/logotype.png";
import {
    flexCenter,
    flexColumn,
    transitionMixin,
} from "./../components/mixins";
import { selectMenu } from "./../providers/redux/menu/menu.selectors";
import { close, open } from "./../providers/redux/menu/menu.slice";
import { scrollTo } from "./../utils/scrollTo";

const LinkNavigationStyles = css`
    & {
        position: relative;
        overflow: hidden;
        margin: 0 15px;
        padding: 6px 0;
        font-size: ${({ theme }) => theme.fz.XXS};
        font-family: ${({ theme }) => theme.ff.Tomica};
        font-weight: ${({ theme }) => theme.fw.bold};
        color: ${({ theme }) => theme.colors.black};
        letter-spacing: 2px;
        text-decoration: none;
        text-transform: uppercase;
        ${transitionMixin("0.4s")};

        i {
            font-size: 1.4em;
        }

        &::before {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            transform: translate(-100%, 0%);
            ${transitionMixin("0.4s")};
            width: 100%;
            height: 2px;
            background-color: ${({ theme }) => theme.colors.black};
        }

        &:hover {
            &::before {
                transform: translate(0%, 0%);
            }
        }

        @media screen and (max-width: 1250px) {
            font-size: ${({ theme }) => theme.fz.XXXS};
        }
    }
`;

const InvertedNavStyles = css`
    background-color: transparent;
`;
const NormalNavStyles = css`
    background-color: rgba(230, 230, 230, 0.5);
`;

const invertNavStyles = (inverted) =>
    inverted ? InvertedNavStyles : NormalNavStyles;

const NavContainer = styled.nav`
    ${flexCenter};
    position: relative;
    z-index: 10;
    width: 100%;
    margin: 80px auto 40px;
    padding: 8px 20px;
    border: 2px solid ${({ theme }) => theme.colors.black};

    ${({ inverted }) => invertNavStyles(inverted)};

    a {
        ${LinkNavigationStyles};

        &.active {
            &::before {
                transform: translate(0%, 0%);
            }
        }
    }

    @media screen and (max-width: 1250px) {
        padding: 8px 5px;
    }

    @media screen and (max-width: 1050px) {
        display: none;
    }
`;

const NavigationMenuStatic = ({ inverted }) => (
    <NavContainer inverted={inverted}>
        <Link to="/" onClick={scrollTo(0)}>
            <i className="fas fa-home"></i>
        </Link>
        <Link to="/o-nas/">O nas</Link>
        <Link to="/artykuly/">Artykuły</Link>
        <Link to="/#nowy_numer" onClick={scrollTo("#nowy_numer")}>
            Bieżący numer
        </Link>
        <Link to="/#newsletter" onClick={scrollTo("#newsletter")}>
            Newsletter
        </Link>
        <Link to="#kontakt" onClick={scrollTo("#kontakt")}>
            Kontakt
        </Link>
    </NavContainer>
);

const NavigationContainer = styled.nav`
    display: grid;
    grid-template-columns: 1fr 1000px 170px 1fr;
    place-content: center;
    position: fixed;
    z-index: 12;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 60px;
    padding: 10px 80px;
    background-color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 1px 0px ${({ theme }) => theme.colors.black};

    @media screen and (max-width: 1250px) {
        grid-template-columns: 1fr 880px 80px 1fr;
    }

    @media screen and (max-width: 1050px) {
        /* DEV */
        ${flexCenter}
        justify-content: space-between;
    }

    @media screen and (max-width: 560px) {
        padding: 10px;
    }
`;

const Menu = styled.div`
    width: 100%;
    height: 100%;
    ${flexCenter};
    justify-content: flex-start;
    grid-column: 2/3;

    .mobile-scroll {
        height: 100%;
        ${flexCenter};
        justify-content: flex-start;
        grid-column: 2/3;
        position: relative;
        overflow: auto;
    }

    @media screen and (max-width: 1050px) {
        display: ${({ opened }) => (opened ? "flex" : "none")};
        box-sizing: content-box;
        flex-direction: column;
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 15;
        transform: translateY(100%);
        width: 100%;
        height: auto;
        max-height: calc(100vh - 60px);
        overflow: hidden;
        padding: 0;
        border-top: 1px solid ${({ theme }) => theme.colors.grey};
        background-color: ${({ theme }) => theme.colors.white};

        .mobile-scroll {
            width: 100%;
            ${flexColumn};
        }
    }
`;

const MenuLogo = styled.img`
    height: 100%;
    max-height: 60px;
    max-width: 100px;
    object-fit: contain;
    margin-right: 50px;

    @media screen and (max-width: 1050px) {
        display: none;
    }
`;

const MobileLogo = styled(MenuLogo)`
    @media screen and (min-width: 1050px) {
        display: none;
    }

    @media screen and (max-width: 1050px) {
        margin: 0 auto;
        display: block;
        height: 60px;
    }
`;

const LinksContainer = styled.div`
    ${flexCenter};
    position: relative;
    width: 100%;

    a {
        ${LinkNavigationStyles};
        margin: 0 30px 0 0 !important;

        &.active {
            &::before {
                transform: translate(0%, 0%);
            }
        }
    }

    @media screen and (max-width: 1050px) {
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
        padding: 20px 40px;

        a {
            padding: 15px 20px;
            width: 100%;
            border-bottom: 1px solid ${({ theme }) => theme.colors.light_grey};

            &::before {
                content: none;
            }
        }
    }
`;

const CurrentNumberContainer = styled.div`
    position: relative;
    width: 100%;
    height: auto;
    padding: 20px;
    ${flexCenter};
    ${flexColumn};
    background-color: ${({ theme }) => theme.colors.light_grey2};

    & > * {
        margin-bottom: 15px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
        text-align: center;
    }

    img {
        display: block;
        width: 80%;
        margin: 0 auto;
    }

    @media screen and (min-width: 1050px) {
        display: none;
    }
`;

const SocialMediaContainer = styled.div`
    grid-column: 3/4;
    ${flexCenter};
    justify-content: flex-end;

    a {
        font-size: ${({ theme }) => theme.fz.S};
        margin: 0 10px;
        color: ${({ theme }) => theme.colors.black};
        ${transitionMixin()};

        &:hover {
            color: ${({ theme }) => theme.colors.grey};
        }
    }
`;

const Hamburger = styled.div`
    position: relative;
    width: 50px;
    height: 50px;

    span {
        position: absolute;
        left: 50%;
        transform: translate(-50%, -50%);
        display: block;
        width: 60%;
        height: 2px;
        background-color: ${({ theme }) => theme.colors.black};
        ${transitionMixin()};

        &:nth-of-type(1) {
            top: 16px;
        }
        &:nth-of-type(2) {
            top: 24px;
        }
        &:nth-of-type(3) {
            top: 32px;
        }
    }

    &.active {
        span {
            top: 50%;

            &:nth-of-type(1) {
                transform: translate(-50%, -50%) rotate(45deg);
            }
            &:nth-of-type(2) {
                opacity: 0;
            }
            &:nth-of-type(3) {
                transform: translate(-50%, -50%) rotate(-45deg);
            }
        }
    }

    @media screen and (min-width: 1050px) {
        display: none;
    }
`;

export const NavigationMenuFloating = (props) => {
    //Hiding and showing menu depending on window.scrollY
    useFloatingNavigation();

    const dispatch = useDispatch();

    const { isOpen, isShown } = useSelector(selectMenu);

    const handleHamburgerClick = () => {
        isOpen ? dispatch(close()) : dispatch(open());
    };

    return (
        isShown && (
            <NavigationContainer>
                <Hamburger
                    onClick={handleHamburgerClick}
                    className={isOpen ? "active" : ""}
                >
                    <span></span>
                    <span></span>
                    <span></span>
                </Hamburger>
                <Link to="/">
                    <MobileLogo src={LogoMini} onClick={scrollTo(0, 0)} />
                </Link>
                <Menu opened={isOpen}>
                    <div className="mobile-scroll">
                        <Link to="/" onClick={scrollTo(0, 0)}>
                            <MenuLogo src={LogoMini} />
                        </Link>
                        <LinksContainer>
                            <Link to="/" onClick={scrollTo(0)}>
                                <i className="fas fa-home"></i>
                            </Link>
                            <Link to="/o-nas/">O nas</Link>
                            <Link to="/artykuly/">Artykuły</Link>
                            <Link
                                to="/#nowy_numer"
                                onClick={scrollTo("#nowy_numer")}
                            >
                                Bieżący numer
                            </Link>
                            <Link
                                to="/#newsletter"
                                onClick={scrollTo("#newsletter")}
                            >
                                Newsletter
                            </Link>
                            <Link to="#kontakt" onClick={scrollTo("#kontakt")}>
                                Kontakt
                            </Link>
                        </LinksContainer>
                        <CurrentNumberContainer>
                            <h6>Patronat merytoryczny</h6>
                            <p>
                                Nad jakością merytoryczną zawartości magazynu
                                czuwa Redakcja czasopisma Współczesna Dietetyka.
                            </p>
                            <a href="https://www.wspolczesnadietetyka.pl/">
                                <picture>
                                    <img
                                        src={wsdLogo}
                                        alt="Bieżący numer magazynu Współczesna Dietetyka"
                                    />
                                </picture>
                            </a>
                        </CurrentNumberContainer>
                    </div>
                </Menu>
                <SocialMediaContainer>
                    <a href="https://www.facebook.com/Zdrowe-Dziecko-Magazyn-dla-rodzic%C3%B3w-104668118492222">
                        <i className="fab fa-facebook-f"></i>
                    </a>
                    <a href="https://www.instagram.com/magazyn_zdrowe_dziecko/">
                        <i className="fab fa-instagram"></i>
                    </a>
                </SocialMediaContainer>
            </NavigationContainer>
        )
    );
};

export default NavigationMenuStatic;
